import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { getDatabaseScope, type DatabaseScope } from '../lib/supabase';
import type { DailyPuzzleGame } from '../lib/dailyPuzzle';
import { getIncompleteDailyGames } from '../lib/gameUtils';
import { save2048Milestone } from '../lib/game2048Milestones';
import { read2048Record, save2048Record } from '../lib/game2048Records';
import { read2048PendingRuns, type Game2048Session } from '../lib/game2048Session';
import { debug } from '../lib/utils';

interface PersistenceOptions {
  session: Game2048Session;
  updateRun: (runId: string, update: (run: Game2048Session) => Game2048Session) => void;
  isPreviewMode: boolean;
}

export const use2048Persistence = ({ session, updateRun, isPreviewMode }: PersistenceOptions) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const authKey = currentUser?.uid ?? 'guest';
  const [scopeState, setScope] = useState<{ scope: DatabaseScope; authKey: string } | null>(null);
  const scope = scopeState?.authKey === authKey ? scopeState.scope : null;
  const [scopeError, setScopeError] = useState(false);
  const [dayError, setDayError] = useState(false);
  const [recordReadError, setRecordReadError] = useState(false);
  const [record, setRecord] = useState<{ principal: string; value: number | null } | null>(null);
  const [newRecord, setNewRecord] = useState<{ principal: string; runId: string; value: number } | null>(null);
  const recordWrites = useRef(0);
  const [incompleteGames, setIncompleteGames] = useState<DailyPuzzleGame[]>(['sudoku', '15puzzle']);
  const [retryVersion, setRetryVersion] = useState(0);
  const [, refresh] = useState(0);
  const inFlight = useRef(new Set<string>());
  const current = useRef({ session, authKey, principal: scope?.principal });
  current.current = { session, authKey, principal: scope?.principal };
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (isPreviewMode) {
      return;
    }
    let cancelled = false;
    setScopeError(false);
    getDatabaseScope().then(nextScope => {
      if (!cancelled) {
        setScope({ scope: nextScope, authKey });
        const run = current.current.session;
        if (run.principal === null) {
          updateRun(run.runId, value => ({ ...value, principal: nextScope.principal }));
        }
      }
    }).catch(error => {
      if (!cancelled) {
        setScopeError(true);
        debug('Error obtaining 2048 database scope:', error);
      }
    });
    return () => { cancelled = true; };
  }, [authKey, isPreviewMode, retryVersion, updateRun]);

  const runId = session.runId;
  const dailyComplete = session.highestTile >= 512;
  const date = session.identity.date;
  useEffect(() => {
    setRecordReadError(false);
    if (isPreviewMode || !scope) {
      return;
    }
    let cancelled = false;
    const writesAtStart = recordWrites.current;
    read2048Record(scope.client).then(value => {
      if (!cancelled && writesAtStart === recordWrites.current) {
        setRecord({ principal: scope.principal, value });
      }
    }).catch(error => {
      if (!cancelled) {
        setRecordReadError(true);
        debug('Error reading 2048 record:', error);
      }
    });
    return () => { cancelled = true; };
  }, [isPreviewMode, retryVersion, scope]);

  useEffect(() => {
    setIncompleteGames(['sudoku', '15puzzle']);
    setDayError(false);
    if (isPreviewMode || !scope || !dailyComplete) {
      return;
    }
    let cancelled = false;
    getIncompleteDailyGames(new Date(date + 'T00:00:00'), '2048', currentUser, scope.client)
      .then(games => { if (!cancelled) { setIncompleteGames(games); } })
      .catch(error => {
        if (!cancelled) {
          setDayError(true);
          debug('Error checking completed 2048 day:', error);
        }
      });
    return () => { cancelled = true; };
  }, [currentUser, dailyComplete, date, isPreviewMode, retryVersion, runId, scope]);

  useEffect(() => {
    if (isPreviewMode || !scope) {
      return;
    }
    const runs = [session, ...read2048PendingRuns().filter(run => run.runId !== session.runId)];
    runs.filter(run => run.principal === scope.principal).forEach(run => {
      run.milestones.filter(milestone => milestone.status === 'pending').forEach(milestone => {
        const key = run.runId + ':' + milestone.difficulty;
        if (inFlight.current.has(key)) {
          return;
        }
        inFlight.current.add(key);
        save2048Milestone(run, milestone, scope, currentUser).then(isPersonalBest => {
          updateRun(run.runId, value => ({
            ...value,
            milestones: value.milestones.map(item => item.difficulty === milestone.difficulty ? { ...item, status: 'saved' } : item)
          }));
          if (mounted.current && current.current.session.runId === run.runId && current.current.principal === scope.principal && isPersonalBest) {
            toast.success(t('toastNewPersonalBest'));
          }
        }).catch(error => {
          updateRun(run.runId, value => ({
            ...value,
            milestones: value.milestones.map(item => item.difficulty === milestone.difficulty ? { ...item, status: 'failed' } : item)
          }));
          debug('Error saving 2048 milestone:', error);
        }).finally(() => {
          inFlight.current.delete(key);
          if (mounted.current) {
            refresh(value => value + 1);
          }
        });
      });
      const recordKey = run.runId + ':record';
      if (run.recordStatus !== 'pending' || inFlight.current.has(recordKey)) {
        return;
      }
      inFlight.current.add(recordKey);
      save2048Record(run, scope).then(result => {
        updateRun(run.runId, value => ({ ...value, recordStatus: 'saved' }));
        if (mounted.current && current.current.principal === scope.principal) {
          recordWrites.current += 1;
          setRecord(previous => ({
            principal: scope.principal,
            value: Math.max(previous?.principal === scope.principal ? previous.value ?? 0 : 0, result.maxTile)
          }));
          setRecordReadError(false);
          if (result.improved && current.current.session.runId === run.runId) {
            setNewRecord({ principal: scope.principal, runId: run.runId, value: result.maxTile });
            toast.success(t('2048.newRecord', { value: result.maxTile }));
          }
        }
      }).catch(error => {
        updateRun(run.runId, value => ({ ...value, recordStatus: 'failed' }));
        debug('Error saving 2048 record:', error);
      }).finally(() => {
        inFlight.current.delete(recordKey);
        if (mounted.current) {
          refresh(value => value + 1);
        }
      });
    });
  }, [currentUser, isPreviewMode, scope, session, t, updateRun]);

  const retry = useCallback(() => {
    if (scope) {
      const runs = [current.current.session, ...read2048PendingRuns()];
      runs.filter(run => run.principal === scope.principal).forEach(run => {
        updateRun(run.runId, value => ({
          ...value,
          recordStatus: value.recordStatus === 'failed' ? 'pending' : value.recordStatus,
          milestones: value.milestones.map(item => item.status === 'failed' ? { ...item, status: 'pending' } : item)
        }));
      });
    }
    setRetryVersion(value => value + 1);
  }, [scope, updateRun]);

  const ownRuns = isPreviewMode || !scope ? [] : [session, ...read2048PendingRuns()].filter(run => run.principal === scope.principal);
  const failed = ownRuns.some(run => run.recordStatus === 'failed' || run.milestones.some(item => item.status === 'failed'));
  const otherOwner = scope !== null && session.principal !== null && session.principal !== scope.principal;
  const error = otherOwner ? t('2048.otherOwner')
    : scopeError ? t('databaseIdentityError')
      : failed || dayError ? t('2048.saveFailed')
        : recordReadError ? t('2048.recordLoadFailed') : null;

  return {
    principal: scope?.principal ?? null,
    canPlay: isPreviewMode || (scope !== null && session.principal === scope.principal),
    personalRecord: scope && record?.principal === scope.principal ? record.value : null,
    recordMessage: scope && newRecord?.principal === scope.principal && newRecord.runId === session.runId
      ? t('2048.newRecord', { value: newRecord.value }) : null,
    incompleteGames,
    error,
    retry
  };
};
